import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Crown, Check, Loader2, Sparkles, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { usePremium, fetchPlans, createCheckout, startLogin } from "@/context/PremiumContext";

const PERKS = [
  "Navigation 100% sans publicité",
  "Voix premium + Le Génie enrichi",
  "Bornes de recharge mondiales (OCM) + planification d'arrêts",
  "Trafic & alertes avancés",
  "Itinéraires multi-arrêts illimités",
  "Zones favorites + préchargement de la carte",
];

// Reusable subscription plan cards (used on /premium and in the account page).
export function PlanCards({ compact = false }) {
  const { isAuthenticated } = usePremium();
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    fetchPlans().then(setPlans).catch(() => setPlans([]));
  }, []);

  const subscribe = async (lookupKey) => {
    if (!isAuthenticated) {
      toast.info("Connectez-vous pour vous abonner");
      startLogin();
      return;
    }
    setBusy(lookupKey);
    try {
      const { checkout_url } = await createCheckout(lookupKey);
      if (checkout_url) window.location.href = checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible de démarrer le paiement");
      setBusy("");
    }
  };

  return (
    <div className={`grid gap-4 ${compact ? "sm:grid-cols-2" : "sm:grid-cols-2"}`} data-testid="plan-cards">
      {plans.map((p) => (
        <Card
          key={p.lookup_key}
          className="relative flex flex-col gap-3 border-amber-500/30 p-5"
          data-testid={`plan-${p.lookup_key}`}
        >
          {p.badge && (
            <Badge className="absolute right-3 top-3 bg-amber-500 text-black hover:bg-amber-500">{p.badge}</Badge>
          )}
          <div className="text-sm font-medium uppercase tracking-wide text-muted-foreground">{p.label}</div>
          <div className="flex items-end gap-1">
            <span className="text-3xl font-extrabold">{p.price}</span>
            <span className="mb-1 text-sm text-muted-foreground">{p.period}</span>
          </div>
          <Button
            onClick={() => subscribe(p.lookup_key)}
            disabled={!!busy}
            className="mt-1 w-full bg-amber-500 text-black hover:bg-amber-400"
            data-testid={`subscribe-${p.lookup_key}`}
          >
            {busy === p.lookup_key ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Crown className="mr-2 h-4 w-4" />}
            S'abonner
          </Button>
        </Card>
      ))}
    </div>
  );
}

export default function Premium() {
  const { isPremium } = usePremium();
  return (
    <div className="mx-auto max-w-3xl px-5 py-10" data-testid="premium-page">
      <Link to="/" className="mb-6 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Retour au GPS
      </Link>
      <div className="mb-6 flex items-center gap-3">
        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-amber-500/15 text-amber-500">
          <Sparkles className="h-6 w-6" />
        </div>
        <div>
          <h1 className="text-2xl font-extrabold">vspo Premium</h1>
          <p className="text-sm text-muted-foreground">Le meilleur de vspo, sans publicité.</p>
        </div>
      </div>

      {isPremium ? (
        <Card className="border-amber-500/40 p-6" data-testid="already-premium">
          <div className="flex items-center gap-2 text-amber-500">
            <Crown className="h-5 w-5" />
            <span className="font-semibold">Votre abonnement Premium est actif. Merci !</span>
          </div>
          <Link to="/compte" className="mt-3 inline-block text-sm text-primary underline">Gérer mon abonnement</Link>
        </Card>
      ) : (
        <>
          <ul className="mb-6 grid gap-2 sm:grid-cols-2">
            {PERKS.map((perk) => (
              <li key={perk} className="flex items-start gap-2 text-sm">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
                <span>{perk}</span>
              </li>
            ))}
          </ul>
          <PlanCards />
          <p className="mt-4 text-xs text-muted-foreground">
            Paiement sécurisé par Stripe. Résiliable à tout moment. TVA gérée automatiquement.
          </p>
        </>
      )}
    </div>
  );
}
